import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { FixedAssetLiquidationService } from "services/fixed-asset-liquidation";
import { numberToWords } from "layouts/accounting-form/appendix-1/cash-voucher/detail/components/printTemplate";
import { getPrintTemplate } from "./components/printTemplate";

const DATE_KEYS = ["documentDate", "decisionDate", "committeeDate", "derecognitionDate", "closingDate"];

const splitDateParts = (value = "") => {
  const [day = "", month = "", year = ""] = String(value).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${String(parts.year)}`;

const validateDateParts = (parts, label, required = false) => {
  if (!hasAnyDatePart(parts)) {
    if (required) throw new Error(`${label} không được để trống`);
    return "";
  }
  if (!hasAllDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) throw new Error(`${label} có tháng không hợp lệ`);
  if (year < 1900 || year > 9999) throw new Error(`${label} có năm không hợp lệ`);
  const maxDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDay) throw new Error(`${label} có ngày không hợp lệ`);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const defaultCommittee = [
  { fullName: "", position: "", representative: "", role: "Trưởng ban" },
  { fullName: "", position: "", representative: "", role: "Ủy viên" },
  { fullName: "", position: "", representative: "", role: "Ủy viên" },
];

const createInitialDateState = () => {
  const today = new Date();
  const d = String(today.getDate()).padStart(2, "0");
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const y = String(today.getFullYear());
  return {
    documentDate: { day: d, month: m, year: y },
    decisionDate: { day: d, month: m, year: y },
    committeeDate: { day: d, month: m, year: y },
    derecognitionDate: { day: "", month: "", year: "" },
    closingDate: { day: d, month: m, year: y },
  };
};

export default function FixedAssetLiquidationDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [dateState, setDateState] = useState(createInitialDateState());
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    decisionNumber: "",
    liquidationReason: "thanh lý tài sản cố định",
    assetNameSpecification: "",
    assetCode: "",
    manufactureYear: "",
    useYear: "",
    assetCardNumber: "",
    originalCost: "",
    accumulatedDepreciation: "",
    remainingValue: "",
    committeeConclusion: "",
    liquidationCost: "",
    liquidationCostText: "",
    recoveryValue: "",
    recoveryValueText: "",
  });
  const [committeeMembers, setCommitteeMembers] = useState(defaultCommittee);
  const [isRemainingValueManual, setIsRemainingValueManual] = useState(false);
  const [isLiquidationCostTextManual, setIsLiquidationCostTextManual] = useState(false);
  const [isRecoveryValueTextManual, setIsRecoveryValueTextManual] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await FixedAssetLiquidationService.getById(id);
        const data = response.data ? response.data : response;
        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          decisionNumber: data.decisionNumber || "",
          liquidationReason: data.liquidationReason || "thanh lý tài sản cố định",
          assetNameSpecification: data.assetNameSpecification || "",
          assetCode: data.assetCode || "",
          manufactureYear: data.manufactureYear || "",
          useYear: data.useYear || "",
          assetCardNumber: data.assetCardNumber || "",
          originalCost: formatVndIntegerInput(data.originalCost || ""),
          accumulatedDepreciation: formatVndIntegerInput(data.accumulatedDepreciation || ""),
          remainingValue: formatVndIntegerInput(data.remainingValue || ""),
          committeeConclusion: data.committeeConclusion || "",
          liquidationCost: formatVndIntegerInput(data.liquidationCost || ""),
          liquidationCostText: data.liquidationCostText || "",
          recoveryValue: formatVndIntegerInput(data.recoveryValue || ""),
          recoveryValueText: data.recoveryValueText || "",
        });
        setIsRemainingValueManual(Boolean(data.remainingValue));
        setIsLiquidationCostTextManual(Boolean(data.liquidationCostText));
        setIsRecoveryValueTextManual(Boolean(data.recoveryValueText));

        setDateState({
          documentDate: splitDateParts(data.documentDate || ""),
          decisionDate: splitDateParts(data.decisionDate || ""),
          committeeDate: splitDateParts(data.committeeDate || ""),
          derecognitionDate: splitDateParts(data.derecognitionDate || ""),
          closingDate: splitDateParts(data.closingDate || ""),
        });

        if (Array.isArray(data.committeeMembers) && data.committeeMembers.length) {
          const normalized = [...defaultCommittee].map((defaultRow, index) => ({
            ...defaultRow,
            ...(data.committeeMembers[index] || {}),
          }));
          setCommitteeMembers(normalized);
        }
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản thanh lý TSCĐ");
      }
    })();
  }, [id]);

  const parsedOriginalCost = useMemo(() => parseVndIntegerInput(header.originalCost), [header.originalCost]);
  const parsedAccumulatedDepreciation = useMemo(
    () => parseVndIntegerInput(header.accumulatedDepreciation),
    [header.accumulatedDepreciation]
  );
  const parsedRemainingValue = useMemo(() => parseVndIntegerInput(header.remainingValue), [header.remainingValue]);
  const parsedLiquidationCost = useMemo(() => parseVndIntegerInput(header.liquidationCost), [header.liquidationCost]);
  const parsedRecoveryValue = useMemo(() => parseVndIntegerInput(header.recoveryValue), [header.recoveryValue]);

  useEffect(() => {
    if (isRemainingValueManual) return;
    const computed = Math.max(0, parsedOriginalCost - parsedAccumulatedDepreciation);
    const formatted = formatVndIntegerInput(computed);
    setHeader((prev) => (prev.remainingValue === formatted ? prev : { ...prev, remainingValue: formatted }));
  }, [parsedOriginalCost, parsedAccumulatedDepreciation, isRemainingValueManual]);

  useEffect(() => {
    if (isLiquidationCostTextManual) return;
    const autoText = parsedLiquidationCost > 0 ? numberToWords(parsedLiquidationCost) : "";
    setHeader((prev) => (prev.liquidationCostText === autoText ? prev : { ...prev, liquidationCostText: autoText }));
  }, [parsedLiquidationCost, isLiquidationCostTextManual]);

  useEffect(() => {
    if (isRecoveryValueTextManual) return;
    const autoText = parsedRecoveryValue > 0 ? numberToWords(parsedRecoveryValue) : "";
    setHeader((prev) => (prev.recoveryValueText === autoText ? prev : { ...prev, recoveryValueText: autoText }));
  }, [parsedRecoveryValue, isRecoveryValueTextManual]);

  const setHeaderField = (field, value) => {
    if (["originalCost", "accumulatedDepreciation", "remainingValue", "liquidationCost", "recoveryValue"].includes(field)) {
      setHeader((prev) => ({ ...prev, [field]: formatVndIntegerInput(value) }));
      return;
    }
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (key, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    const sanitized = field === "year" ? raw.slice(0, max) : raw.slice(-max);
    setDateState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: sanitized,
      },
    }));
  };

  const padDateField = (key, field) => {
    if (field === "year") return;
    setDateState((prev) => {
      if (!prev[key][field]) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          [field]: String(prev[key][field]).padStart(2, "0"),
        },
      };
    });
  };

  const updateCommitteeMember = (index, field, value) => {
    setCommitteeMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const debitAccount = String(header.debitAccount || "").trim();
    const creditAccount = String(header.creditAccount || "").trim();
    const decisionNumber = String(header.decisionNumber || "").trim();
    const liquidationReason = String(header.liquidationReason || "").trim();
    const assetNameSpecification = String(header.assetNameSpecification || "").trim();
    const assetCode = String(header.assetCode || "").trim();
    const manufactureYear = String(header.manufactureYear || "").trim();
    const useYear = String(header.useYear || "").trim();
    const assetCardNumber = String(header.assetCardNumber || "").trim();
    const committeeConclusion = String(header.committeeConclusion || "").trim();
    const liquidationCostText = String(header.liquidationCostText || "").trim();
    const recoveryValueText = String(header.recoveryValueText || "").trim();

    const documentDate = validateDateParts(dateState.documentDate, "Ngày biên bản", true);
    const decisionDate = validateDateParts(dateState.decisionDate, "Ngày quyết định", true);
    const committeeDate = validateDateParts(dateState.committeeDate, "Ngày kết luận ban thanh lý", false);
    const derecognitionDate = validateDateParts(dateState.derecognitionDate, "Ngày ghi giảm sổ TSCĐ", false);
    const closingDate = validateDateParts(dateState.closingDate, "Ngày ký biên bản", false);

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!decisionNumber) throw new Error("Căn cứ quyết định số không được để trống");
    if (!assetNameSpecification) throw new Error("Tên, ký mã hiệu, quy cách (cấp hạng) TSCĐ không được để trống");

    const normalizedCommittee = committeeMembers
      .map((member, index) => ({
        fullName: String(member.fullName || "").trim(),
        position: String(member.position || "").trim(),
        representative: String(member.representative || "").trim(),
        role: String(member.role || "").trim() || (index === 0 ? "Trưởng ban" : "Ủy viên"),
      }))
      .filter((member) => member.fullName || member.position || member.representative || member.role);

    if (!normalizedCommittee.length) {
      throw new Error("Ban thanh lý TSCĐ phải có ít nhất 1 thành viên");
    }

    if (normalizedCommittee.some((member) => !member.fullName)) {
      throw new Error("Thành viên ban thanh lý phải có họ tên");
    }

    return {
      unit,
      department,
      documentDate,
      documentNumber,
      debitAccount,
      creditAccount,
      decisionNumber,
      decisionDate,
      liquidationReason,
      committeeMembers: normalizedCommittee,
      assetNameSpecification,
      assetCode,
      manufactureYear,
      useYear,
      assetCardNumber,
      originalCost: Math.max(0, parsedOriginalCost),
      accumulatedDepreciation: Math.max(0, parsedAccumulatedDepreciation),
      remainingValue: Math.max(0, parsedRemainingValue),
      committeeConclusion,
      committeeDate,
      liquidationCost: Math.max(0, parsedLiquidationCost),
      liquidationCostText,
      recoveryValue: Math.max(0, parsedRecoveryValue),
      recoveryValueText,
      derecognitionDate,
      closingDate,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (id) {
        await FixedAssetLiquidationService.update(id, payload);
        toast.success("Cập nhật biên bản thanh lý TSCĐ thành công");
      } else {
        await FixedAssetLiquidationService.create(payload);
        toast.success("Tạo biên bản thanh lý TSCĐ thành công");
      }
      navigation("/templates/fixed-asset-liquidation");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      header: {
        ...header,
        documentDate: hasAllDatePart(dateState.documentDate) ? composeDate(dateState.documentDate) : "",
        decisionDate: hasAllDatePart(dateState.decisionDate) ? composeDate(dateState.decisionDate) : "",
        committeeDate: hasAllDatePart(dateState.committeeDate) ? composeDate(dateState.committeeDate) : "",
        derecognitionDate: hasAllDatePart(dateState.derecognitionDate) ? composeDate(dateState.derecognitionDate) : "",
        closingDate: hasAllDatePart(dateState.closingDate) ? composeDate(dateState.closingDate) : "",
        originalCost: parsedOriginalCost,
        accumulatedDepreciation: parsedAccumulatedDepreciation,
        remainingValue: parsedRemainingValue,
        liquidationCost: parsedLiquidationCost,
        recoveryValue: parsedRecoveryValue,
      },
      committeeMembers,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 38,
      textAlign: "center",
    },
    sectionTitle: { fontWeight: 700, marginTop: 10 },
  };

  const renderDateInputs = (key, disabled = false) => (
    <>
      Ngày{" "}
      <input
        disabled={disabled}
        style={s.inputDate}
        value={dateState[key].day}
        onChange={(event) => setDateField(key, "day", event.target.value)}
        onBlur={() => padDateField(key, "day")}
      />{" "}
      tháng{" "}
      <input
        disabled={disabled}
        style={s.inputDate}
        value={dateState[key].month}
        onChange={(event) => setDateField(key, "month", event.target.value)}
        onBlur={() => padDateField(key, "month")}
      />{" "}
      năm{" "}
      <input
        disabled={disabled}
        style={{ ...s.inputDate, width: 60 }}
        value={dateState[key].year}
        onChange={(event) => setDateField(key, "year", event.target.value)}
      />
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input disabled={containView} style={{ ...s.inputDotted, width: 220 }} value={header.unit} onChange={(event) => setHeaderField("unit", event.target.value)} />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </SoftBox>
                  <SoftBox textAlign="center">
                    <div>
                      <b>Mẫu số 02 - TSCĐ</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Kèm theo Thông tư số 99/2025/TT-BTC</div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </SoftBox>
                </SoftBox>

                <SoftBox textAlign="center" mt={2}>
                  <div style={{ fontWeight: 700, fontSize: 30 }}>BIÊN BẢN THANH LÝ TSCĐ</div>
                  <div style={{ fontStyle: "italic", marginTop: 4 }}>{renderDateInputs("documentDate", containView)}</div>
                </SoftBox>

                <SoftBox mt={1} display="flex" justifyContent="flex-end">
                  <div style={{ lineHeight: 1.8 }}>
                    <div>
                      Số:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.documentNumber}
                        onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                      />
                    </div>
                    <div>
                      Nợ:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.debitAccount}
                        onChange={(event) => setHeaderField("debitAccount", event.target.value)}
                      />
                    </div>
                    <div>
                      Có:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.creditAccount}
                        onChange={(event) => setHeaderField("creditAccount", event.target.value)}
                      />
                    </div>
                  </div>
                </SoftBox>

                <div style={{ marginTop: 8 }}>
                  Căn cứ Quyết định số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 130 }}
                    value={header.decisionNumber}
                    onChange={(event) => setHeaderField("decisionNumber", event.target.value)}
                  />{" "}
                  {renderDateInputs("decisionDate", containView)} về việc{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 260 }}
                    value={header.liquidationReason}
                    onChange={(event) => setHeaderField("liquidationReason", event.target.value)}
                  />
                  .
                </div>

                <div style={s.sectionTitle}>I. Ban thanh lý TSCĐ gồm:</div>
                {committeeMembers.map((member, index) => (
                  <div key={`member-${index}`} style={{ marginTop: 4 }}>
                    Ông/Bà:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 180 }}
                      value={member.fullName}
                      onChange={(event) => updateCommitteeMember(index, "fullName", event.target.value)}
                    />{" "}
                    Chức vụ{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120 }}
                      value={member.position}
                      onChange={(event) => updateCommitteeMember(index, "position", event.target.value)}
                    />{" "}
                    Đại diện{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 150 }}
                      value={member.representative}
                      onChange={(event) => updateCommitteeMember(index, "representative", event.target.value)}
                    />{" "}
                    {member.role}
                  </div>
                ))}

                <div style={s.sectionTitle}>II. Tiến hành thanh lý TSCĐ:</div>
                <div style={{ marginTop: 4 }}>
                  - Tên, ký mã hiệu, quy cách (cấp hạng) TSCĐ:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 430 }}
                    value={header.assetNameSpecification}
                    onChange={(event) => setHeaderField("assetNameSpecification", event.target.value)}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Số hiệu TSCĐ/Mã TSCĐ:{" "}
                  <input disabled={containView} style={{ ...s.inputDotted, width: 260 }} value={header.assetCode} onChange={(event) => setHeaderField("assetCode", event.target.value)} />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Năm sản xuất:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 110, textAlign: "center" }}
                    value={header.manufactureYear}
                    onChange={(event) => setHeaderField("manufactureYear", event.target.value.replace(/\D/g, "").slice(0, 4))}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Năm đưa vào sử dụng:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 110, textAlign: "center" }}
                    value={header.useYear}
                    onChange={(event) => setHeaderField("useYear", event.target.value.replace(/\D/g, "").slice(0, 4))}
                  />{" "}
                  Số thẻ TSCĐ{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 150 }}
                    value={header.assetCardNumber}
                    onChange={(event) => setHeaderField("assetCardNumber", event.target.value)}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Nguyên giá TSCĐ:{" "}
                  <input disabled={containView} style={{ ...s.inputDotted, width: 180, textAlign: "right" }} value={header.originalCost} onChange={(event) => setHeaderField("originalCost", event.target.value)} />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Giá trị hao mòn đã trích đến thời điểm thanh lý:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                    value={header.accumulatedDepreciation}
                    onChange={(event) => setHeaderField("accumulatedDepreciation", event.target.value)}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Giá trị còn lại của TSCĐ:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                    value={header.remainingValue}
                    onChange={(event) => {
                      setIsRemainingValueManual(true);
                      setHeaderField("remainingValue", event.target.value);
                    }}
                  />
                </div>

                <div style={s.sectionTitle}>III. Kết luận của Ban thanh lý TSCĐ:</div>
                <div style={{ marginTop: 4 }}>
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: "100%" }}
                    value={header.committeeConclusion}
                    onChange={(event) => setHeaderField("committeeConclusion", event.target.value)}
                  />
                </div>
                <div style={{ marginTop: 6 }}>{renderDateInputs("committeeDate", containView)}</div>

                <div style={s.sectionTitle}>IV. Kết quả thanh lý TSCĐ:</div>
                <div style={{ marginTop: 4 }}>
                  - Chi phí thanh lý TSCĐ:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 170, textAlign: "right" }}
                    value={header.liquidationCost}
                    onChange={(event) => setHeaderField("liquidationCost", event.target.value)}
                  />{" "}
                  (viết bằng chữ){" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 260 }}
                    value={header.liquidationCostText}
                    onChange={(event) => {
                      setIsLiquidationCostTextManual(true);
                      setHeaderField("liquidationCostText", event.target.value);
                    }}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  - Giá trị thu hồi:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 170, textAlign: "right" }}
                    value={header.recoveryValue}
                    onChange={(event) => setHeaderField("recoveryValue", event.target.value)}
                  />{" "}
                  (viết bằng chữ){" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 260 }}
                    value={header.recoveryValueText}
                    onChange={(event) => {
                      setIsRecoveryValueTextManual(true);
                      setHeaderField("recoveryValueText", event.target.value);
                    }}
                  />
                </div>
                <div style={{ marginTop: 4 }}>- Đã ghi giảm sổ TSCĐ {renderDateInputs("derecognitionDate", containView)}</div>
                <div style={{ marginTop: 4 }}>{renderDateInputs("closingDate", containView)}</div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/fixed-asset-liquidation")}>
                    TRỞ VỀ
                  </SoftButton>

                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In biên bản
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "Đang lưu..." : id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
